import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Fade,
  Grow
} from '@mui/material';
import {
  CheckCircle,
  CheckCircleOutline,
  Error,
  Receipt,
  Person,
  Email,
  CalendarToday,
  CreditCard
} from '@mui/icons-material';
import { paymentApi, PaymentTokenManager } from '../services/paymentApi';
import './OrderSummary.scss';

const OrderSummary = () => {
  const { token: urlToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get token from URL params or query parameters
  const token = urlToken !== 'default' ? urlToken : searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [productFeatures, setProductFeatures] = useState(null);

  useEffect(() => {
    if (token) {
      verifyPaymentAndFetchDetails();
    } else {
      setError('Payment token not found in URL');
      setLoading(false);
    }
  }, [token]);

  const verifyPaymentAndFetchDetails = async () => {
    try {
      console.log('🔍 Verifying payment token:', token);
      
      // Verify payment token
      const paymentResult = await paymentApi.verifyPaymentToken(token);
      
      if (!paymentResult.verified) {
        throw new Error(paymentResult.error || 'Payment verification failed');
      }

      console.log('✅ Payment verified:', paymentResult.payment);
      setPaymentData(paymentResult.payment);

      // Store payment data for later use in registration
      PaymentTokenManager.storeToken(token);
      PaymentTokenManager.storePaymentData(paymentResult.payment);

      // Fetch product features for the purchased tier
      if (paymentResult.payment.productTier) {
        try {
          const features = await paymentApi.getProductFeatures(paymentResult.payment.productTier.toLowerCase());
          setProductFeatures(features);
        } catch (featuresError) {
          console.warn('Could not fetch product features:', featuresError);
          // Continue without features - not critical
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('❌ Error verifying payment:', err);
      setError(err.message || 'Failed to verify payment');
      setLoading(false);
    }
  };

  const handleContinueToAccountSetup = () => {
    // Redirect to registration flow with payment verified flag
    navigate('/registration?payment_verified=true&userType=Homeowner');
  };

  const handleContactSupport = () => {
    window.open('mailto:support@homedesignguild.com?subject=Order%20Summary%20Issue&body=Order%20Token:%20' + token, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProductDisplayName = (tier) => {
    const names = {
      guild: 'Guild Membership',
      guild_plus: 'Guild+ Membership',
      Guild: 'Guild Membership',
      GuildPlus: 'Guild+ Membership'
    };
    return names[tier] || tier;
  };

  const renderProductFeatures = () => {
    if (!productFeatures?.features) return null;

    const features = productFeatures.features;
    const featureList = [
      { key: 'projectGallery', label: 'Project Gallery', value: features.projectGallery },
      { key: 'guildGuideArticles', label: 'Guild Guide Articles', value: features.guildGuideArticles },
      { key: 'powerfulFilters', label: 'Powerful Filters', value: features.powerfulFilters ? 'Included' : 'Not included' },
      { key: 'ideaBooks', label: 'Idea Books', value: features.ideaBooks ? 'Included' : 'Not included' },
      { key: 'smartGuildMatching', label: 'Smart Guild Matching', value: features.smartGuildMatching ? 'Included' : 'Not included' },
      { key: 'education', label: 'Educational Content', value: features.education ? 'Included' : 'Not included' }
    ];

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1c1c1c' }}>
          Your Membership Includes:
        </Typography>
        <List dense>
          {featureList.map((feature, index) => (
            <Grow key={feature.key} in={true} timeout={500 + (index * 100)}>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleOutline sx={{ color: '#4caf50', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText 
                  primary={feature.label}
                  secondary={typeof feature.value === 'string' ? feature.value : null}
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.85rem' }}
                />
              </ListItem>
            </Grow>
          ))}
        </List>
        
        {features.description && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {features.description}
            </Typography>
          </Alert>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md" className="order-summary-page">
        <Box className="loading-container">
          <CircularProgress size={60} sx={{ mb: 2, color: '#00A06D' }} />
          <Typography variant="h6">Verifying your payment...</Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we confirm your order details
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" className="order-summary-page">
        <Fade in={true}>
          <Card className="summary-card error-card">
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Error sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Payment Verification Failed
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {error}
              </Typography>
              
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  If you completed your payment successfully, please contact our support team 
                  with your order token: <strong>{token}</strong>
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleContactSupport}
                  sx={{ minWidth: 140 }}
                >
                  Contact Support
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/registration')}
                  sx={{ minWidth: 140 }}
                >
                  Start Over
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" className="order-summary-page">
      <Fade in={true} timeout={800}>
        <Card className="summary-card success-card">
          <CardContent sx={{ p: 4 }}>
            
            {/* Success Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Grow in={true} timeout={600}>
                <CheckCircle sx={{ fontSize: 100, color: '#4caf50', mb: 2 }} />
              </Grow>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#1c1c1c' }}>
                Payment Successful!
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Thank you for joining Home Design Guild
              </Typography>
              <Chip 
                label="Order Confirmed" 
                color="success" 
                variant="outlined"
                sx={{ fontSize: '0.9rem', fontWeight: 600 }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Order Details */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1c1c1c', mb: 3 }}>
                <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                Order Summary
              </Typography>

              <Box className="order-details-grid">
                <Box className="detail-item">
                  <Typography variant="body2" color="text.secondary">Product</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {getProductDisplayName(paymentData?.productTier)}
                  </Typography>
                </Box>

                <Box className="detail-item">
                  <Typography variant="body2" color="text.secondary">Amount Paid</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                    ${paymentData?.paymentAmount}
                  </Typography>
                </Box>

                <Box className="detail-item">
                  <Typography variant="body2" color="text.secondary">
                    <CalendarToday sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Order Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(new Date().toISOString())}
                  </Typography>
                </Box>

                <Box className="detail-item">
                  <Typography variant="body2" color="text.secondary">
                    <CreditCard sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Order Token
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {token}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Customer Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1c1c1c' }}>
                <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                Customer Information
              </Typography>
              
              <Box className="customer-info">
                {paymentData?.contactName && (
                  <Box className="info-item">
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{paymentData.contactName}</Typography>
                  </Box>
                )}
                
                <Box className="info-item">
                  <Typography variant="body2" color="text.secondary">
                    <Email sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Email
                  </Typography>
                  <Typography variant="body1">{paymentData?.contactEmail}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Product Features */}
            {renderProductFeatures()}

            <Divider sx={{ my: 4 }} />

            {/* Continue Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Ready to Complete Your Account?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Continue to set up your account and start enjoying your membership benefits.
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                onClick={handleContinueToAccountSetup}
                sx={{
                  py: 2,
                  px: 6,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 25px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                Continue to Account Setup
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                This will take you through email verification and profile setup
              </Typography>
            </Box>

          </CardContent>
        </Card>
      </Fade>
    </Container>
  );
};

export default OrderSummary;
